import qs from "qs";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useLocation } from "react-router";
import { useRequisitionOrderEditActive } from "hooks/useRequisitionOrderCancel";
import { userSelector } from "store/auth";
import { globalsSelector } from "store/globals";
import { noteSelector } from "store/note";
import { segmentsSelector } from "store/segments";
import { uiSelector } from "store/ui";
import {
	calculateAgeFromBirthDate,
	formatAgeForNoteOutput,
	formatNotePatientFullName,
	getMedicalSegmentFromSegments,
	isHospitalCategory,
	isJustmedsTenant,
	isOpenedInIframe,
	parsePatientDateOfBirthMoment,
} from "utils/helpers";
import LocaleLink from "views/components/LocaleLink";
import { useSubmitNoteIntent } from "views/contexts/SubmitNoteIntentContext";
import {
	isMedicalOrPharmacyNoteFormSegment,
	planSubmitNoteIntent,
	shouldUseSubmitNoteSignatureModal,
} from "utils/helpers/noteSubmit";

const HeaderNoteInfo = ({
	bg = "",
	allowSaveNote,
	allowDeleteNote,
	allowNewReferral,
	allowNewOrder,
	isDemo,
	isElectronicFormOrder,
	isReferral,
}) => {
	const { t } = useTranslation();
	const { state: locationState, search, pathname } = useLocation();

	// redux global state
	const note = useSelector(noteSelector);
	const isRequisitionOrderEdit = useRequisitionOrderEditActive(isElectronicFormOrder, isReferral);
	const user = useSelector(userSelector);
	const globals = useSelector(globalsSelector);
	const segments = useSelector(segmentsSelector);
	const { isNoteLoading = false, isQuestionsLoading = false, isNotePatientLoading = false } = useSelector(uiSelector);

	// constants
	const isCurrentCategoryHospitalCategory = useMemo(
		() => isHospitalCategory(user?.segment_category),
		[user?.segment_category]
	);
	const isInIframe = useMemo(() => isOpenedInIframe(), []);
	const medicalSegment = useMemo(() => getMedicalSegmentFromSegments(segments), [segments]);
	const isCurrentSegmentMedicalSegment = medicalSegment?.id === globals?.segment?.id || false;

	const { setIntent, isSubmitting, requestNoteSubmit } = useSubmitNoteIntent();
	const useSubmitNoteSignatureModal = shouldUseSubmitNoteSignatureModal({
		isElectronicFormOrder,
		isReferral,
		note,
	});
	const useSubmitOrderModal = isElectronicFormOrder || isReferral;
	const useDoneActionLabel =
		useSubmitOrderModal || isMedicalOrPharmacyNoteFormSegment(globals, segments);

	const handleNoteSubmitClick = () => {
		if (useSubmitOrderModal) return;
		setIntent(planSubmitNoteIntent);
		if (!useSubmitNoteSignatureModal) requestNoteSubmit();
	};

	return (
		<div className={`root-header-notes-info py-md-2 ${!isDemo ? bg || "" : "bg-white"}`}>
			<div className="container px-0 px-md-gutter overflow-hidden">
				<div className="row align-items-stretch gx-0 gx-md-2">
					<div
						className={`col-12 ${
							!isNoteLoading &&
							!isQuestionsLoading &&
							!isNotePatientLoading &&
							note?.id &&
							(isDemo ||
								(!isElectronicFormOrder &&
									!isReferral &&
									isCurrentSegmentMedicalSegment &&
									((allowNewReferral && isCurrentCategoryHospitalCategory) || allowNewOrder)))
								? "col-md-auto"
								: "col-md-10 col-lg-9"
						}`}>
						<div className="py-1 py-xl-0 px-gutter px-md-0 h-100">
							{isNoteLoading || isQuestionsLoading || isNotePatientLoading || !note?.id ? (
								<div className="row h-100 align-items-center gx-lg-2 gy-2 gy-lg-0">
									<div className="col-6 col-md-4 col-xl-4">
										<p className="placeholder-glow mb-0">
											<small className="row gx-0 flex-nowrap">
												<strong className="text-uppercase me-1 col-auto">
													{t("titles.patient_name")}:
												</strong>
												<span className="placeholder bg-secondary col">&nbsp;</span>
											</small>
										</p>
									</div>
									<div className={`${isInIframe ? "col-12 col-md-6" : "col-6 col-md-4 col-xl-2"}`}>
										<p className="placeholder-glow mb-0">
											<small className="row gx-0 flex-nowrap">
												<strong className="text-uppercase me-1 col-auto">
													{t("titles.gender")}:
												</strong>
												<span className="placeholder bg-secondary col">&nbsp;</span>
											</small>
										</p>
									</div>
									<div className="col-6 col-md-4 col-xl-2">
										<p className="placeholder-glow mb-0">
											<small className="row gx-0 flex-nowrap">
												<strong className="text-uppercase me-1 col-auto">
													{t("titles.dob")}:
												</strong>
												<span className="placeholder bg-secondary col">&nbsp;</span>
											</small>
										</p>
									</div>
									{!isElectronicFormOrder && !isReferral && (
										<div
											className={`${isInIframe ? "col-12 col-md-6" : "col-6 col-md-4 col-xl-2"}`}>
											<p className="placeholder-glow mb-0">
												<small className="row gx-0 flex-nowrap">
													<strong className="text-uppercase me-1 col-auto">
														{t("titles.note_id")}:
													</strong>
													<span className="placeholder bg-secondary col">&nbsp;</span>
												</small>
											</p>
										</div>
									)}
								</div>
							) : (
								<div className="row h-100 align-items-center gx-lg-2 gy-2 gy-lg-0">
									<div className="col-6 col-md-auto">
										<p className="white-space-nowrap text-truncate mb-0">
											<small>
												<strong className="text-uppercase me-1">
													{t("titles.patient_name")}:
												</strong>
												<span>
													{isDemo
														? "Olive Yew"
														: formatNotePatientFullName(note, locationState) || ""}
												</span>
											</small>
										</p>
									</div>
									<div className={`${isInIframe ? "col-12 col-md-6" : "col-6 col-md-auto"}`}>
										<p className={`${isInIframe ? "" : "white-space-nowrap"} mb-0`}>
											<small>
												<strong className="text-uppercase me-1">{t("titles.gender")}:</strong>
												<span className="text-capitalize">
													{locationState?.gender ||
														note?.patient_plain_text?.gender ||
														note?.gender ||
														"-"}
												</span>
											</small>
										</p>
									</div>
									<div className="col-6 col-md-auto">
										<p className="white-space-nowrap mb-0">
											<small>
												<strong className="text-uppercase me-1">{t("titles.dob")}:</strong>
												<span>
													{isDemo
														? "July 1 1970"
														: (() => {
																const rawDob =
																	locationState?.date_of_birth ||
																	note?.patient_plain_text?.date_of_birth ||
																	note?.date_of_birth;
																const m = parsePatientDateOfBirthMoment(rawDob);
																if (!m.isValid()) {
																	return "-";
																}
																const age = calculateAgeFromBirthDate(rawDob);
																const ageStr =
																	age &&
																	formatAgeForNoteOutput(age, {
																		yearFormat: t("titles.year_symbol"),
																		monthFormat: t("titles.month_symbol"),
																		dayFormat: t("titles.day_symbol"),
																		allowProperNoun: false,
																	});
																return `${m.format("MMMM D YYYY")} (${ageStr || "-"})`;
														  })()}
												</span>
											</small>
										</p>
									</div>
									{!isElectronicFormOrder && !isReferral && (
										<div className={`${isInIframe ? "col-12 col-md-6" : "col-6 col-md-auto"}`}>
											<p
												className={`${
													isInIframe ? "" : "white-space-nowrap text-truncate"
												} mb-0`}>
												<small>
													<strong className="text-uppercase me-1">
														{t("titles.note_id")}:
													</strong>
													<span>{note?.short_id || ""}</span>
												</small>
											</p>
										</div>
									)}
									{isElectronicFormOrder && (
										<div className={`${isInIframe ? "col-12 col-md-6" : "col-6 col-md-auto"}`}>
											<p
												className={`${
													isInIframe ? "" : "white-space-nowrap text-truncate"
												} mb-0`}>
												<small>
													<strong className="text-uppercase me-1">
														{t("requisition.order_id")}:
													</strong>
													<span>{note?.short_id || ""}</span>
												</small>
											</p>
										</div>
									)}
								</div>
							)}
						</div>
					</div>
					{(allowSaveNote ||
						allowDeleteNote ||
						(!isElectronicFormOrder &&
							!isReferral &&
							isCurrentSegmentMedicalSegment &&
							((allowNewReferral && isCurrentCategoryHospitalCategory) || allowNewOrder))) && (
						<>
							<div className="col-auto col-md-12 col-lg-auto">
								<hr className="border border-top-0 border-secondary my-0 d-none d-md-block d-lg-none" />
								<div className="vr border border-left-0 border-secondary h-100 op-20 d-md-none d-lg-block"></div>
							</div>
							<div className="col-12 col-md ms-auto ">
								<div className="row gx-0 gx-md-2 align-items-stretch flex-nowrap h-100">
									{Boolean(
										!isElectronicFormOrder &&
											!isReferral &&
											isCurrentSegmentMedicalSegment &&
											((allowNewReferral && isCurrentCategoryHospitalCategory) ||
												allowNewOrder) &&
											!isNoteLoading &&
											!isQuestionsLoading &&
											!isNotePatientLoading
									) && (
										<>
											{allowNewReferral && isCurrentCategoryHospitalCategory && (
												<>
													<div className="col col-md-12 col-lg">
														<div className="p-1 py-md-0 py-lg-1 h-100">
															<LocaleLink
																to={`/referrals/store?${qs.stringify({
																	patient_id: note?.patient_id || note?.patient?.id,
																	back_to: `${pathname}${search || ""}`,
																})}`}
																className="btn btn-link btn-sm py-1 h-100 w-100 text-decoration-none text-uppercase white-space-nowrap rounded-0 d-flex align-items-center justify-content-center">
																new referral
															</LocaleLink>
														</div>
													</div>
													<div className="col-auto col-md-12 col-lg-auto">
														<hr className="border border-top-0 border-secondary my-0 d-none d-md-block d-lg-none" />
														<div className="vr border border-left-0 border-secondary h-100 op-20 d-md-none d-lg-block"></div>
													</div>
												</>
											)}
											{allowNewOrder && (
												<>
													<div className="col col-md-12 col-lg">
														<div className="p-1 py-md-0 py-lg-1 h-100">
															<LocaleLink
																to={`/orders/store?${qs.stringify({
																	patient_id: note?.patient_id || note?.patient?.id,
																	back_to: `${pathname}${search || ""}`,
																})}`}
																className="btn btn-link btn-sm py-1 h-100 w-100 text-decoration-none text-uppercase white-space-nowrap rounded-0 d-flex align-items-center justify-content-center">
																new order
															</LocaleLink>
														</div>
													</div>
													<div className="col-auto col-md-12 col-lg-auto">
														<hr className="border border-top-0 border-secondary my-0 d-none d-md-block d-lg-none" />
														<div className="vr border border-left-0 border-secondary h-100 op-20 d-md-none d-lg-block"></div>
													</div>
												</>
											)}
										</>
									)}
									{Boolean(
										allowDeleteNote &&
											!pathname.includes("/patient-intake") &&
											(isNoteLoading ||
												isQuestionsLoading ||
												!note?.id ||
												!note?.completed ||
												(isJustmedsTenant() && isOpenedInIframe() && !note?.terminal_completed))
									) && (
										<>
											<div className="col col-md-12 col-lg">
												<div className="p-1 py-md-0 py-lg-1 h-100">
													{isNoteLoading || isQuestionsLoading || !note?.id ? (
														<button
															type="button"
															className="btn btn-link btn-sm py-1 link-danger h-100 w-100 text-uppercase white-space-nowrap rounded-0 d-flex align-items-center justify-content-center placeholder-glow"
															disabled>
															<span className="placeholder bg-danger w-50">&nbsp;</span>
														</button>
													) : (
														<button
															type="button"
															className="btn btn-link btn-sm py-1 link-danger h-100 w-100 text-decoration-none text-uppercase white-space-nowrap rounded-0 d-flex align-items-center justify-content-center"
															data-bs-toggle="modal"
															data-bs-target="#deleteNoteModal">
															{isJustmedsTenant() && isOpenedInIframe()
																? "Change minor ailment"
																: `Cancel ${
																		isReferral
																			? "referral"
																			: note?.medical_form_label || "note"
																  }`}
														</button>
													)}
												</div>
											</div>
											{allowSaveNote && (
												<div className="col-auto col-md-12 col-lg-auto">
													<hr className="border border-top-0 border-secondary my-0 d-none d-md-block d-lg-none" />
													<div className="vr border border-left-0 border-secondary h-100 op-20 d-md-none d-lg-block"></div>
												</div>
											)}
										</>
									)}
									{allowSaveNote && (
										<>
											{isRequisitionOrderEdit && (
												<>
													<div className="col col-md-12 col-lg">
														<div className="p-1 py-md-0 py-lg-1 h-100">
															{isNoteLoading || isQuestionsLoading || !note?.id ? (
																<button
																	type="button"
																	className="btn btn-link btn-sm py-1 link-danger h-100 w-100 text-uppercase white-space-nowrap rounded-0 d-flex align-items-center justify-content-center placeholder-glow"
																	disabled>
																	<span className="placeholder bg-danger w-50">
																		&nbsp;
																	</span>
																</button>
															) : (
																<button
																	type="button"
																	className="btn btn-link btn-sm py-1 link-danger h-100 w-100 text-decoration-none text-uppercase white-space-nowrap rounded-0 d-flex align-items-center justify-content-center"
																	data-bs-toggle="modal"
																	data-bs-target="#cancelOrderModal"
																	aria-label={t("requisition.remove_draft")}>
																	{t("requisition.remove_draft")}
																</button>
															)}
														</div>
													</div>
													<div className="col-auto col-md-12 col-lg-auto">
														<hr className="border border-top-0 border-secondary my-0 d-none d-md-block d-lg-none" />
														<div className="vr border border-left-0 border-secondary h-100 op-20 d-md-none d-lg-block"></div>
													</div>
												</>
											)}
											<div className="col col-md-12 col-lg">
												<div className="p-1 py-md-0 py-lg-1 h-100">
													{isNoteLoading || isQuestionsLoading || !note?.id ? (
														<button
															type="button"
															className="btn btn-link btn-sm py-1 h-100 w-100 text-decoration-none text-uppercase white-space-nowrap rounded-0 d-flex align-items-center justify-content-center placeholder-glow"
															disabled>
															<span className="placeholder bg-primary w-50">&nbsp;</span>
														</button>
													) : (
														<button
															type="button"
															className="btn btn-link btn-sm py-1 h-100 w-100 text-decoration-none text-uppercase white-space-nowrap rounded-0 d-flex align-items-center justify-content-center"
															onClick={handleNoteSubmitClick}
															disabled={isSubmitting}
															{...(useSubmitNoteSignatureModal
																? {
																		"data-bs-toggle": "modal",
																		"data-bs-target": "#submitNoteModal",
																  }
																: {})}
															{...(useSubmitOrderModal
																? {
																		"data-bs-toggle": "modal",
																		"data-bs-target": "#submitOrderModal",
																  }
																: {})}>
															{useDoneActionLabel
																? t("actions.done")
																: t("actions.submit")}
															{Boolean(isSubmitting) && (
																<span
																	className="spinner-border spinner-border-sm ms-2"
																	role="status">
																	<span className="visually-hidden">{`${t(
																		"content.loading"
																	)}...`}</span>
																</span>
															)}
														</button>
													)}
												</div>
											</div>
										</>
									)}
								</div>
							</div>
						</>
					)}
					{isDemo && (
						<>
							<div className="col-12 d-lg-none">
								<hr className="border border-top-0 border-secondary my-0" />
							</div>
							<div className="col-auto d-none d-lg-block">
								<div className="vr border border-left-0 border-secondary h-100 op-20"></div>
							</div>
							<div className="col-12 col-md ms-auto align-self-center">
								<p className="mb-0 small lh-1 py-3 py-lg-0 text-center text-lg-start">
									<small>
										<em>This is a dummy data to help to try the note process</em>
									</small>
								</p>
							</div>
						</>
					)}
				</div>
			</div>
		</div>
	);
};

export default HeaderNoteInfo;
